import React, { PropsWithChildren, useEffect } from "react";
import { useAuthenticated, useAuthProvider } from "react-admin";
import { AuthManager } from "../../../common/auth/Auth";
import {
  LiveTrackTokenApiFactory,
  LiveTrackVehicleDto,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";

interface Props {
  vehicleId: number;
  setConnectionToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  setError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setVehicle: React.Dispatch<
    React.SetStateAction<LiveTrackVehicleDto | undefined>
  >;
}

export const AuthenticatedLocationLiveTrack = ({
  vehicleId,
  children,
  setConnectionToken,
  setError,
  setLoading,
  setVehicle,
}: PropsWithChildren<Props>) => {
  useAuthenticated();
  const authProvider = useAuthProvider() as AuthManager;
  const axios = useAxiosInstance();

  useEffect(() => {
    if (!axios) {
      return;
    }

    let isMounted = true;

    setLoading(true);
    authProvider
      .getAccessToken()
      .then((token) => {
        if (isMounted) {
          setConnectionToken(token);
        }
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        console.error("Unable to get the live tracking access token", error);
        setConnectionToken(undefined);
        setError(
          !!error?.message ? error.message.toString() : error?.toString()
        );
      });

    LiveTrackTokenApiFactory(undefined, axios?.defaults.baseURL, axios)
      .apiAppLivetracktokenIdVehicleforlivetrackGet(vehicleId)
      .then((response) => {
        if (isMounted && response.data) {
          setVehicle(response.data);
        }
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        console.error("Unable to get the requested vehicle data", error);
        setError(
          !!error?.message ? error.message.toString() : error?.toString()
        );
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    authProvider,
    axios,
    setConnectionToken,
    setError,
    setLoading,
    setVehicle,
    vehicleId,
  ]);

  useEffect(() => {
    return () => {
      setConnectionToken(undefined);
    };
  }, [setConnectionToken]);

  return <>{children}</>;
};
